/**
 * IDENTIFY invitation screen — "enter your invitation" step of onboarding, per the `sInvite`
 * screen in the onboarding prototype
 * (docs/superpowers/design-reference/1815-onboarding/onboarding-prototype.dc.html, lines
 * 218-241, id `#cn-invite`): an Eyebrow reading "Identify", the serif heading "Enter your
 * invitation.", the body copy ("If you were given an invitation, enter it here. No code?
 * You can continue without one." — the `!this.props.inviteRequired` branch of `inviteBody`,
 * line 662), an INVITATION CODE labeled text input (placeholder "CN-XXXX-XXXX") with a
 * "Paste" button beside it (line 226-227), a primary "Redeem invitation" button (line 236),
 * the "I don't have one — continue without an invitation" text link (line 238), and a muted
 * hint "You can also scan the QR on your invitation card." (line 240).
 *
 * Pure presentation: it owns no invitation-redemption state of its own, it only renders
 * whatever `InvitationState` the kernel-driven flow hands it and emits `redeem` / `skip` for
 * the host to act on.
 *
 * Mirrors `../../pairing/pairing-view.ts` and `./connect-discovery.ts`'s idiom: `extends
 * HTMLElement`, `set state()` -> `#render()`, shadow root + inline `<style>` using
 * `var(--token, <light-literal-fallback>)` so the shell's token sheet (adopted at the
 * `<node-app-shell>` root) drives theming while still standing alone if this element is ever
 * mounted outside that tree, plus the same reduced-motion/forced-colors guards. Not
 * self-registered here (like the two mirrored files) — the host and the test file each
 * register the tag with a `customElements.get` guard.
 *
 * SECURITY (docs/development/CLIENT_NODE_CONVENTIONS.md §13): the invitation code is a
 * secret. It lives ONLY in the transient `<input>` element's `.value` DOM property (never
 * set via `setAttribute`, so it never serializes into markup/outerHTML) and in the
 * `redeem` event's `detail`. It is never written to an attribute, `dataset`, `id`, a URL,
 * `localStorage`, or logged/console'd, and this element persists no state of its own across
 * renders that could retain it.
 */
export class NodeOnboardingIdentifyInvitationElement extends HTMLElement {
    #state = {};
    set state(value) {
        this.#state = value;
        this.#render();
    }
    get state() {
        return this.#state;
    }
    connectedCallback() {
        this.#render();
    }
    #render() {
        const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
        root.replaceChildren();
        const style = document.createElement("style");
        style.textContent = `
      :host { display: block; font-family: var(--ui-font, Inter, system-ui, sans-serif); color: var(--ink-hi, #0A0A0A); }
      section {
        display: flex; flex-direction: column; gap: 16px;
        padding: clamp(20px, 6vw, 36px) clamp(16px, 5vw, 32px);
        background: var(--bg-raised, #FFFFFF);
        border: 1px solid var(--ui-line-soft, rgba(0, 0, 0, 0.06));
        border-radius: var(--ui-radius-lg, 14px);
        box-shadow: var(--ui-shadow-1, 0 1px 1px rgba(10, 10, 10, 0.05), 0 10px 24px -14px rgba(10, 10, 10, 0.25));
      }
      .eyebrow {
        font: 600 10px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--ink-low, #888888);
        padding-bottom: 12px; border-bottom: 1px solid var(--hr, rgba(10, 10, 10, 0.07));
      }
      h1 {
        margin: 0; font: 400 clamp(1.75rem, 6vw, 2.125rem)/1.05 var(--ui-serif, "Instrument Serif", serif);
      }
      p.body { margin: 0; font-size: 13.5px; line-height: 1.6; color: var(--ink-mid, #545454); }
      .field { display: flex; flex-direction: column; gap: 6px; }
      .field label {
        font: 400 10px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--ink-low, #888888);
      }
      .code-row { display: flex; gap: 8px; }
      .invitation-code {
        flex: 1; min-height: 2.375rem; box-sizing: border-box;
        border: 1px solid var(--hr-strong, rgba(10, 10, 10, 0.12)); border-radius: var(--ui-radius, 10px);
        padding: .5rem .75rem; color: var(--ink-hi, #0A0A0A); background: var(--bg-0, #FFFFFF);
        font: 400 .9375rem/1.5 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.08em; text-transform: uppercase;
      }
      .invitation-code::placeholder { color: var(--ink-mid, #545454); text-transform: none; letter-spacing: normal; }
      .paste {
        flex-shrink: 0; min-height: 2.375rem; border: 1px solid var(--hr-strong, rgba(10, 10, 10, 0.12));
        border-radius: var(--ui-radius, 10px); padding: .5rem .9rem;
        font: 500 .875rem/1.2 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--ink-hi, #0A0A0A); background: var(--bg-0, #FFFFFF); cursor: pointer;
        transition: background 0.18s var(--ease-out, ease-out), border-color 0.18s var(--ease-out, ease-out);
      }
      .paste:hover:not(:disabled) { background: var(--bg-hover, rgba(10, 10, 10, 0.045)); }
      [role=alert] {
        margin: 0; font-size: 12.5px; line-height: 1.5;
        color: light-dark(var(--c-coral-deep, #B85551), var(--c-coral, #DF6662));
      }
      .redeem {
        min-height: 2.5rem; border: 1px solid var(--ink-hi, #0A0A0A); border-radius: var(--ui-radius, 10px);
        padding: .6rem 1rem; font: 500 .875rem/1.2 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--bg-0, #FFFFFF); background: var(--ink-hi, #0A0A0A); cursor: pointer; width: 100%;
        transition: background 0.18s var(--ease-out, ease-out), border-color 0.18s var(--ease-out, ease-out);
      }
      .redeem:hover:not(:disabled) { background: var(--c-coral-deep, #B85551); border-color: var(--c-coral-deep, #B85551); }
      .redeem:disabled, .paste:disabled { opacity: 0.6; cursor: default; }
      .paste:focus-visible, .redeem:focus-visible, .skip:focus-visible, .invitation-code:focus-visible {
        outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px;
      }
      .skip {
        align-self: flex-start; background: none; border: none; padding: 0; cursor: pointer;
        font: 400 12.5px/1.4 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--ink-low, #888888); text-decoration: underline;
      }
      .skip:disabled { opacity: 0.6; cursor: default; text-decoration: none; }
      .hint { margin: 0; font-size: 11.5px; color: var(--ink-xlo, #B5B5B5); }
      @media (prefers-reduced-motion: reduce) {
        * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; animation: none !important; }
      }
      @media (forced-colors: active) {
        section, .invitation-code, .paste, .redeem { border: 1px solid CanvasText; }
      }
    `;
        const section = document.createElement("section");
        const eyebrow = document.createElement("p");
        eyebrow.className = "eyebrow";
        eyebrow.textContent = "Identify";
        const heading = document.createElement("h1");
        heading.textContent = "Enter your invitation.";
        const body = document.createElement("p");
        body.className = "body";
        body.textContent =
            "If you were given an invitation, enter it here. No code? You can continue without one.";
        const { error, working = false } = this.#state;
        const field = document.createElement("div");
        field.className = "field";
        const label = document.createElement("label");
        label.htmlFor = "identify-invitation-code";
        label.textContent = "INVITATION CODE";
        const codeRow = document.createElement("div");
        codeRow.className = "code-row";
        const input = document.createElement("input");
        input.type = "text";
        input.id = "identify-invitation-code";
        input.className = "invitation-code";
        input.placeholder = "CN-XXXX-XXXX";
        input.spellcheck = false;
        input.autocomplete = "off";
        input.disabled = working;
        const pasteButton = document.createElement("button");
        pasteButton.type = "button";
        pasteButton.className = "paste";
        pasteButton.textContent = "Paste";
        pasteButton.disabled = working;
        pasteButton.addEventListener("click", () => {
            void this.#pasteInto(input);
        });
        codeRow.append(input, pasteButton);
        field.append(label, codeRow);
        if (error !== undefined) {
            const alert = document.createElement("p");
            alert.setAttribute("role", "alert");
            alert.setAttribute("aria-live", "assertive");
            alert.textContent = error;
            field.append(alert);
        }
        const redeemButton = document.createElement("button");
        redeemButton.type = "button";
        redeemButton.className = "redeem";
        redeemButton.textContent = "Redeem invitation";
        redeemButton.disabled = working;
        redeemButton.addEventListener("click", () => {
            const code = input.value.trim();
            this.dispatchEvent(new CustomEvent("redeem", { bubbles: true, composed: true, detail: { code } }));
        });
        const skipLink = document.createElement("button");
        skipLink.type = "button";
        skipLink.className = "skip";
        skipLink.textContent = "I don’t have one — continue without an invitation";
        skipLink.disabled = working;
        skipLink.addEventListener("click", () => {
            this.dispatchEvent(new CustomEvent("skip", { bubbles: true, composed: true }));
        });
        const hint = document.createElement("p");
        hint.className = "hint";
        hint.textContent = "You can also scan the QR on your invitation card.";
        section.append(eyebrow, heading, body, field, redeemButton, skipLink, hint);
        root.append(style, section);
    }
    async #pasteInto(input) {
        const clipboard = navigator.clipboard;
        if (clipboard === undefined || typeof clipboard.readText !== "function")
            return;
        try {
            const text = await clipboard.readText();
            input.value = text;
        }
        catch {
            // Clipboard access denied or unavailable — leave the field untouched, never throw.
        }
    }
}
//# sourceMappingURL=identify-invitation.js.map